export interface RealtimeEventMap {
  'quotes.update':         { symbol: string; bid?: number; ask?: number; last?: number; ts: string };
  'cqg.state':             { state: 'Disconnected' | 'Connecting' | 'Live' | 'Error'; accounts: string[]; lastError?: string | null; reason?: string | null };
  // Same shape as cqg.state — the Rithmic browser-relay session state. Handled via setQueryData in the
  // dialog/display hook (NOT useRealtimeInvalidation), exactly like cqg.state.
  'rithmic.state':         { state: 'Disconnected' | 'Connecting' | 'Live' | 'Error'; accounts: string[]; lastError?: string | null; reason?: string | null };
  // serverTs/flowId: optional latency-measurement fields stamped by the BE (Date.now()-serverTs = e2e ms).
  'trades.changed':        { kind: 'added' | 'updated' | 'deleted'; count: number; serverTs?: number; flowId?: string };
  'correlations.changed':  { account: string | null; symbols?: string[] | null; count: number; serverTs?: number; flowId?: string };
  // Position layer rebuilt (v2_positions/v2_position_fills re-projected). Same payload shape as
  // correlations.changed — published by the BE rebuild coalescer alongside it.
  'positions.changed':     { account: string | null; symbols?: string[] | null; count: number; serverTs?: number; flowId?: string };
  'user.profile.updated':  Record<string, never>;
  // Stripe webhook processed (checkout completed, plan changed, renewed, cancelled, payment failed,
  // or a manual override toggled) — the BE re-publishes this any time the caller's effective
  // subscription may have changed. `kind` names which webhook fired (e.g. 'checkout.completed',
  // 'plan.changed', 'cancelled') — the FE doesn't branch on it (see
  // `useSubscriptionChangedInvalidation`, called unconditionally from `SubscriptionGuard`, which
  // invalidates ['me'] regardless), but it's real wire data (not `{}`) and worth carrying in the
  // type for anything that logs/inspects the push later.
  'subscription.changed':  { kind: string };
  'import.progress':       { job: string; processed: number; total: number };
  // CQG historical-order snapshot fully delivered (live fills follow). Drives the connect dialog's
  // import-progress bar to 100% — CQG never reports a total, so this is the real "done" marker.
  'cqg.import.complete':   Record<string, never>;
  // CQG server UserMessage forwarded by the BE (`UserMessageHandler`). `type`: 1=CRITICAL_ERROR,
  // 2=WARNING, 3=INFO, 4=LOG. No FE consumer today — registered so the push isn't DROPPED with a
  // "No client method with the name 'cqg.message' found" SignalR warning (it fired on benign
  // messages like CQG's "symbol resolution error" for a stale order). Surface to the user later if a
  // message type warrants it (product decision).
  'cqg.message':           { type: number; text: string; source?: string };
  // §14h — BE publishes after upserting a UserAccount row (Manual on signup, CQG on logon, Rithmic
  // on the 303 account list). The FE invalidates `['trades','accounts']`; AccountInitializer reacts.
  // Union kept in sync with `AccountInfo.provider` (features/trades/hooks/useTradeMetadata.ts) —
  // inline because core/realtime stays free of feature imports.
  'accounts.updated':      { name: string; provider: 'manual' | 'cqg' | 'imported' | 'rithmic' };
  // Live CQG account snapshot push (same shape as GET /api/v1/account/summary minus the
  // statement-derived startingBalance/startingBalanceDate). `useAccountSummary` writes it into the
  // query cache keyed by `account` so the "Account (CQG)" card + Return % update without polling.
  'account.summary':       {
    account: string;
    currency: string | null;
    balance: string | null;
    equity: string | null;
    ote: string | null;
    mvo: string | null;
    totalMargin: string | null;
    positionMargin: string | null;
    purchasingPower: string | null;
    profitLoss: string | null;
    unrealizedProfitLoss: string | null;
    asOf: string;
  };
  // AI Backtester — a queued run finished (completed or failed). The FE invalidates the
  // ['ai-backtest'] query family; the results page's status poll is the fallback for missed pushes.
  'aibacktest.run.completed': { strategyId: string; runId: string; status: 'completed' | 'failed' | string };
  // AI credits granted (2026-08-14) — published by the Stripe `checkout.session.completed` webhook
  // the moment a credit-pack purchase is applied to the ledger. `balance` is the balance AFTER the
  // grant; the FE doesn't read it (it invalidates the credits query and refetches the truth), but
  // it's real wire data and worth carrying in the type.
  'credits.changed':       { balance: number };
  // Live CQG open-positions push — the FULL current list (snapshot semantics). `useCqgPositions`
  // replaces its cache with this (filtered to the scoped account). Mirrors the BE `CqgPositionResponse`
  // / the FE `CqgPosition` (kept inline so core/realtime stays free of feature imports).
  'positions.cqg': Array<{
    account: string;
    symbol: string;
    netQty: string;
    side: 'long' | 'short' | 'flat';
    avgOpenPrice: string;
    currency: string | null;
    asOf: string;
  }>;
}

export type RealtimeEventName = keyof RealtimeEventMap;

/**
 * Every server-push event name, as a runtime list. `RealtimeProvider` registers a SignalR client
 * method for ALL of these at connection start — a push with no registered client method is DROPPED
 * by @microsoft/signalr (with a "No client method with the name 'X' found" warning), so lazily
 * registering on first subscribe silently lost pushes that arrived while no consumer was mounted
 * (e.g. `positions.cqg` while the dashboard showed the Closed tab, `account.summary` off the PnL
 * page, `cqg.import.complete` after the connect dialog closed).
 */
export const REALTIME_EVENT_NAMES = [
  'quotes.update',
  'cqg.state',
  'rithmic.state',
  'trades.changed',
  'correlations.changed',
  'positions.changed',
  'user.profile.updated',
  'subscription.changed',
  'import.progress',
  'cqg.import.complete',
  'cqg.message',
  'accounts.updated',
  'account.summary',
  'aibacktest.run.completed',
  'credits.changed',
  'positions.cqg',
] as const satisfies readonly RealtimeEventName[];

// Compile-time exhaustiveness guard: if a RealtimeEventMap key is missing from
// REALTIME_EVENT_NAMES, this assignment fails to typecheck (the type collapses to `never`).
type MissingRealtimeEvents = Exclude<RealtimeEventName, (typeof REALTIME_EVENT_NAMES)[number]>;
export const REALTIME_EVENT_NAMES_ARE_EXHAUSTIVE: MissingRealtimeEvents[] extends never[] ? true : never = true;
